import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, switchMap } from 'rxjs/operators';
import { PriorityViewModel } from 'app/models/PriorityViewModel';
import { reduceLength } from 'app/extensionmethods/StringExtensions';
import Utils from '../../../shared/utils';
import { PartnerFileService } from '../../../services/partner-file.service';
import { ConfirmBox } from '../../shared/ConfirmBox/confirm-box';

@Component({
	selector: 'app-components-manager-partnerfiles',
	templateUrl: './PartnerFiles.component.html'
})
export class PartnerFilesComponent implements IActivatable {
    private static readonly objectUrlRevokeDelayMs = 60000;

    files: Boo.Objects.PartnerFile[] = [];
    partnerId: number;
    maxNameDisplayLength: number;

    constructor(private partnerFileService: PartnerFileService, private confirmBox: ConfirmBox, private destroyRef: DestroyRef) { }

    activate(params: IPartnerFilesViewModelActivateParams): JQueryPromise<void> {
        this.partnerId = params.partnerId;
        this.maxNameDisplayLength = params.maxNameDisplayLength || 80;
        return this.loadFiles();
    }

    loadFiles(): JQueryPromise<any> {
        return Utils.wrapDfd(this.partnerFileService.get(this.partnerId))
            .then((files: Boo.Objects.PartnerFile[]) => {
                files.forEach(file => {
                    file.Name = reduceLength(file.Name, this.maxNameDisplayLength);
                });
                this.files = files;
            })
            .fail((displayMessage: string) => {
                toastr.error(displayMessage);
            });
    }

    upload(): void {
        PriorityViewModel.show('app-components-shared-uploadfile', {
            uploadHandler: (file: File) => Utils.wrapDfd(this.partnerFileService.upload(this.partnerId, file)),
            acceptAllFileTypes: true
        })
            .then((partnerFile: Boo.Objects.PartnerFile) => {
                partnerFile.Name = reduceLength(partnerFile.Name, this.maxNameDisplayLength);
                this.files.splice(0, 0, partnerFile);
            });
    }

    view(file: Boo.Objects.PartnerFile): void {
        const tab = window.open('', '_blank');
        if (!tab) {
            toastr.error('Unable to open the file. Please allow popups for this site.');
            return;
        }

        Utils.wrapDfd(this.partnerFileService.download(file.FileId))
            .then((blob: Blob) => {
                const url = window.URL.createObjectURL(blob);
                tab.location.href = url;
                setTimeout(() => window.URL.revokeObjectURL(url), PartnerFilesComponent.objectUrlRevokeDelayMs);
            })
            .fail(() => {
                tab.close();
                toastr.error('Unable to open the file.');
            });
    }

    delete(file: Boo.Objects.PartnerFile): void {
        this.confirmBox.confirm(`Are you sure you want to delete "${file.Name}"?`, { title: 'Delete File' })
            .pipe(
                filter(confirmed => confirmed),
                switchMap(() => this.partnerFileService.save({ ...file, IsDeleted: true })),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => this.loadFiles(),
                error: (displayMessage: string) => toastr.error(displayMessage)
            });
    }
}

interface IPartnerFilesViewModelActivateParams {
    partnerId: number;
    maxNameDisplayLength?: number;
}
